import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';
import qs from 'qs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';
import { encodeQueryParams, JsonParam } from 'use-query-params';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import AnalyticsQueryContainer from 'dashboard/components/AnalyticsQueryContainer';
import RecencyLabel from 'dashboard/components/AnalyticsQueryContainer/RecencyLabel';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import LoadingOverlay from 'dashboard/components/LoadingOverlay';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { findPeriodEnd, Period } from 'dashboard/lib/unixTime';
import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import { CheckStatus } from 'dashboard/models/check';
import type { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { DynamicFilters, MetricsGroupBy } from 'dashboard/models/metrics-verification';
import { Template } from 'dashboard/models/template';
import { InquiriesFilterParams } from 'dashboard/redux/actions/inquiries';
import {
  FilterRulesForType,
  initialState,
  VerificationGranularitiesByType,
  VerificationMetricsState,
  VerificationTemplatesGroupedByType,
} from 'dashboard/redux/slices/metrics-verification';
import CheckMetricsWithFilters from 'dashboard/views/DashboardVerificationAnalyticsFilteredChecksView/CheckMetricsWithFilters';
import { TYPE_FILTER_OPTIONS } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/DashboardVerificationAnalyticsVolumeView';
import {
  FilterDimensions,
  FilteredVerificationType,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import { VerificationType } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';
import { Iso8601DateString } from 'lib/types';
import { nameDisplay } from 'lib/utils/template';
import { CheckName } from 'lib/verificationCheck';

const SearchAndFilterContainer = styled.div`
  display: grid;
  align-items: end;
  overflow: visible;
  text-overflow: ellipsis;
  grid-template-columns: minmax(100px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr);
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.widescreen })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

export interface DashboardVerificationAnalyticsFilteredChecksViewProps
  extends Pick<
    VerificationMetricsState,
    | 'verificationChecksOverTime'
    | 'filters'
    | 'lastQueryFilters'
    | 'filterRules'
    | 'checksLastQueryFilterRules'
    | 'verificationGranularities'
  > {
  handleTemplateFilterUpdate: (templateIds: string[]) => void;
  handleMatchRequirementUpdate: (req: string[]) => void;
  handleVerificationTypeUpdate: (verificationTypes: string[]) => void;
  handleGroupByFilterToggle?: (value: boolean) => void;
  handleAddAndRule: (type: FilteredVerificationType, orRuleIndex: number) => void;
  handleUpdateAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => void;
  handleDeleteAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
  ) => void;
  handleAddOrRule: (type: FilteredVerificationType) => void;
  handleVerificationTemplateFilterUpdate: (
    type: VerificationType,
    verificationTemplates: string[],
  ) => void;
  loading: boolean;
  loadVerificationCheckMetrics: () => Promise<void>;
  queriedAt: Iso8601DateString;
  updatedAt: Iso8601DateString;
  templates: (Template | InquiryTemplate)[];
  handleBulkRulesUpdate: (filterRules: Record<VerificationType, FilterRulesForType>) => void;
  verificationTemplates: VerificationTemplatesGroupedByType;
  error: boolean;
  dynamicFilterOptions: DynamicFilters;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleUpdateGranularity: (type: VerificationType, period: Period) => void;
  checksLastGranularities: VerificationGranularitiesByType;
  resetFiltersToInitialState: () => void;
  openCreateQueryTemplateModal: () => void;
  queryTemplates: AnalyticsQueryTemplate[];
  selectedChecksQueryTemplate: AnalyticsQueryTemplate | null;
  handleLoadQueryTemplate: (template: AnalyticsQueryTemplate) => void;
  unsetSelectedChecksQueryTemplate: () => void;
  isQueryTemplateModified: boolean;
}

export const CHECK_REQUIREMENT_OPTIONS = [
  {
    label: 'Required',
    value: 'required',
  },
  {
    label: 'Non required',
    value: 'non-required',
  },
];

const DashboardVerificationAnalyticsFilteredChecksView = ({
  filters,
  lastQueryFilters,
  handleDateUpdateV2,
  handleTemplateFilterUpdate,
  loading,
  loadVerificationCheckMetrics,
  handleMatchRequirementUpdate,
  handleVerificationTypeUpdate,
  handleGroupByFilterToggle,
  handleAddAndRule,
  handleAddOrRule,
  handleUpdateAndRule,
  handleUpdateGranularity,
  handleDeleteAndRule,
  handleVerificationTemplateFilterUpdate,
  queriedAt,
  updatedAt,
  templates,
  filterRules,
  verificationChecksOverTime,
  handleBulkRulesUpdate,
  checksLastQueryFilterRules,
  verificationTemplates,
  error,
  dynamicFilterOptions,
  verificationGranularities,
  checksLastGranularities,
  resetFiltersToInitialState,
  openCreateQueryTemplateModal,
  queryTemplates,
  handleLoadQueryTemplate,
  selectedChecksQueryTemplate,
  isQueryTemplateModified,
  unsetSelectedChecksQueryTemplate,
}: DashboardVerificationAnalyticsFilteredChecksViewProps) => {
  const {
    createdAtStart,
    createdAtEnd,
    templateIds,
    checkRequirements,
    verificationTypes,
    groupBy,
  } = filters;
  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: createdAtStart ? DateTime.fromISO(createdAtStart, { setZone: true }) : undefined,
      endDate: createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined,
    };
  }, [createdAtStart, createdAtEnd]);

  const checkRequirementLabelTextMapper = useLabelTextMapper(
    CHECK_REQUIREMENT_OPTIONS,
    'requirements',
    {
      emptyIsAll: true,
    },
  );

  const templateFilterOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: nameDisplay(template),
        value: template.id,
      })),
    [templates],
  );

  const templateFilterLabelTextMapper = useLabelTextMapper(templateFilterOptions, 'templates', {
    emptyIsAll: true,
  });

  const verificationTypeFilterLabelTextMapper = useLabelTextMapper(TYPE_FILTER_OPTIONS, 'types', {
    emptyIsAll: true,
  });

  // Track whether we've opened a new filter panel (thus, we cannot compare the current + previous filters)
  const [filtersOpen, handleOpenFilters] = useState(false);

  //Deeplinking
  const handleDeepLink = useCallback(
    (
      check: string,
      label: number,
      nameMap: Record<string, CheckName>,
      status: CheckStatus,
      groupByEnabled: boolean,
    ) => {
      // We'll need to remove the suffixes from the legend/data for us to categorize this as a CheckName
      const parsedCheck = nameMap[check];
      // Only when we can find the check + have a visible data payload do we deep link
      if (Object.values(CheckName).includes(parsedCheck as CheckName)) {
        const createdAtISO = DateTime.fromSeconds(label).toISO();
        const filter: InquiriesFilterParams = {
          checks: [
            {
              check: parsedCheck as CheckName,
              status: status,
            },
          ],
          createdAtStart: createdAtISO,
          createdAtEnd: findPeriodEnd(
            createdAtISO,
            verificationChecksOverTime.period,
          ) as Iso8601DateString,
          templateIds: lastQueryFilters?.templateIds,
        };

        // When the group by button is selected, we'll go to All inquiries as opposed to all Verifications
        window.open(
          `/dashboard/${groupByEnabled ? 'inquiries' : 'verifications'}?${qs.stringify(
            encodeQueryParams(
              {
                filter: JsonParam,
              },
              {
                filter,
              },
            ),
          )}`,
          '_blank',
        );
      }
    },
    [createdAtStart, createdAtEnd, lastQueryFilters, verificationChecksOverTime, lastQueryFilters],
  );

  // On an initial page click, we want to load metrics
  const loaded = queriedAt != null;

  const requiredReload =
    verificationChecksOverTime != null &&
    !isEqual(filterRules, checksLastQueryFilterRules) &&
    !filtersOpen;

  const requiredPeriodReload = !isEqual(verificationGranularities, checksLastGranularities);

  useEffect(() => {
    //When alternating between tabs, we'll need to ensure that the current set of filter rules matches the last query of filterRules
    // Ensure that Filters aren't currently being edited
    if ((requiredReload || !loaded || requiredPeriodReload) && !error) {
      // We'll want to let the query parameters kick off another load. This is because we disable the FE features
      // that allow for the user to fire off another query.
      loadVerificationCheckMetrics();
    }
  }, [loaded, error, filterRules, filtersOpen]);

  const renderSearchAndFilterWrapper = () => (
    <SearchAndFilterContainer>
      <AnalyticsDatePicker
        dataTest='metrics-checks-view__date-range'
        handleDatesChangeV2={handleDateUpdateV2}
        dateRange={dateRange}
        disabled={loading}
      />
      <div>
        <FormControlLabel>Verification type</FormControlLabel>
        <MultiSelect
          value={verificationTypes}
          selectOptionValues={TYPE_FILTER_OPTIONS}
          labelTextMapper={verificationTypeFilterLabelTextMapper}
          onChange={handleVerificationTypeUpdate}
          disabled={loading}
          size='md'
          data-test='metrics-verifications-inquiry-templates-modal__select'
        />
      </div>
      {templates.length > 1 && (
        <div>
          <FormControlLabel>Inquiry templates</FormControlLabel>
          <MultiSelect
            value={templateIds}
            selectOptionValues={templateFilterOptions}
            labelTextMapper={templateFilterLabelTextMapper}
            onChange={handleTemplateFilterUpdate}
            disabled={loading}
            size='md'
            data-test='metrics-checks-view__select-template'
          />
        </div>
      )}
      <div>
        <FormControlLabel>Check requirements</FormControlLabel>
        <MultiSelect
          value={checkRequirements}
          selectOptionValues={CHECK_REQUIREMENT_OPTIONS}
          labelTextMapper={checkRequirementLabelTextMapper}
          onChange={handleMatchRequirementUpdate}
          disabled={loading}
          size='md'
          data-test='metrics-checks-view__select-check-requirement'
        />
      </div>
    </SearchAndFilterContainer>
  );

  return (
    <>
      <AnalyticsQueryContainer
        selectedQueryTemplate={selectedChecksQueryTemplate}
        queryTemplates={queryTemplates}
        onLoad={handleLoadQueryTemplate}
        onRun={loadVerificationCheckMetrics}
        useGroupByToggle={true}
        groupByLabel='Group by Inquiries'
        groupByValue={groupBy === MetricsGroupBy.Inquiry}
        groupByOnChange={handleGroupByFilterToggle}
        loading={loading}
        onSave={openCreateQueryTemplateModal}
        onClear={resetFiltersToInitialState}
        isQueryTemplateModified={isQueryTemplateModified}
        unsetSelectedTemplate={unsetSelectedChecksQueryTemplate}
        recencyLabel={
          <>{queriedAt != null && <RecencyLabel queriedAt={queriedAt} recencyIso={updatedAt} />}</>
        }
      >
        {renderSearchAndFilterWrapper()}
      </AnalyticsQueryContainer>
      {loading && <LoadingOverlay />}
      {!isEqual(verificationChecksOverTime, initialState.verificationChecksOverTime) && (
        <PersonaErrorBoundary
          onCatch={(error) =>
            captureMessage('Verification Analytics failed to render', 'error', {
              error,
              component: 'DashboardVerificationAnalyticsFilteredChecksView',
            })
          }
        >
          <CheckMetricsWithFilters
            verificationChecksOverTime={verificationChecksOverTime}
            lastQueryFilters={lastQueryFilters}
            queriedAt={queriedAt}
            filterRules={filterRules}
            handleAddAndRule={handleAddAndRule}
            handleAddOrRule={handleAddOrRule}
            handleDeleteAndRule={handleDeleteAndRule}
            handleUpdateAndRule={handleUpdateAndRule}
            handleLoadMetrics={loadVerificationCheckMetrics}
            handleBulkRulesUpdate={handleBulkRulesUpdate}
            checksLastQueryFilterRules={checksLastQueryFilterRules}
            handleUpdateGranularity={handleUpdateGranularity}
            handleOpenFilters={handleOpenFilters}
            handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
            onDeepLink={handleDeepLink}
            verificationTemplates={verificationTemplates}
            dynamicFilterOptions={dynamicFilterOptions}
            verificationGranularities={verificationGranularities}
          />
        </PersonaErrorBoundary>
      )}
    </>
  );
};

export default DashboardVerificationAnalyticsFilteredChecksView;
